import React, { useRef, useState } from "react";
import { Box, Button, useNavigate } from "../../Collections/Imports";
import Alert from "@mui/material/Alert";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useApiCallMutation } from "../../Services/ApiCallService";
import { pingtreeApiEndPoints } from "../../Config/APIsEndpoints";
import SaveDialog from "./SaveDialog";
import { apiErrorText, isLdsAdmin, parseTreeFile } from "./treeLogic";

const readUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("userInfo"));
  } catch (e) {
    return null;
  }
};

/**
 * Creates a new tree from a JSON file (bulk setup, CU-LS-M1-057): checks the file, shows the API's dry-run result,
 * then creates the tree and opens it in the builder. The file's treeUniqueId is ignored: this always creates.
 */
const ImportNewTreeButton = ({ treeType }) => {
  const navigate = useNavigate();
  const input = useRef(null);
  const [callImport] = useApiCallMutation();
  const [pending, setPending] = useState(null); // {doc, result}
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  if (!isLdsAdmin(readUserInfo())) return null; // the API allows tree import to LDS admins only

  const send = async (doc) => {
    const body = await callImport({ endPoint: pingtreeApiEndPoints.importTree, method: "POST", data: { data: doc } })
      .unwrap()
      .catch((e) => ({ failed: e }));
    if (!body || body.failed) return { error: apiErrorText(body?.failed, "The API did not answer.") };
    if (body.error) {
      const lines = (body.data?.errors || []).map((e) => `${e.path}: ${e.message}`);
      return { error: typeof body.data === "string" ? body.data : body.data?.message || "Import failed", lines };
    }
    return { data: body.data };
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const { doc, errors } = parseTreeFile(String(reader.result));
      if (errors.length) {
        setMessage({ severity: "error", text: `${file.name} was not imported.`, lines: errors.map((e) => `${e.path}: ${e.message}`) });
        return;
      }
      if (doc.tree.pingTreeType === undefined) {
        setMessage({ severity: "error", text: `${file.name} has no tree.pingTreeType; a new tree needs one ("${treeType}").` });
        return;
      }
      if (String(doc.tree.pingTreeType).toLowerCase() !== String(treeType).toLowerCase()) {
        setMessage({ severity: "error", text: `${file.name} is a ${doc.tree.pingTreeType} tree; open the ${doc.tree.pingTreeType} tab to import it.` });
        return;
      }
      const { treeUniqueId, ...tree } = doc.tree;
      const createDoc = { format: doc.format, tree, campaigns: doc.campaigns };
      setSaving(true);
      const checked = await send({ ...createDoc, dryRun: true });
      setSaving(false);
      if (checked.error) {
        setMessage({ severity: "error", text: checked.error, lines: checked.lines });
        return;
      }
      setMessage(null);
      setPending({ doc: createDoc, result: checked.data });
    };
    reader.readAsText(file);
  };

  const onConfirm = async () => {
    setSaving(true);
    const created = await send({ ...pending.doc, dryRun: false });
    setSaving(false);
    setPending(null);
    if (created.error) {
      setMessage({ severity: "error", text: created.error, lines: created.lines });
      return;
    }
    navigate(`/ping-tree/${created.data.treeUniqueId}`);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Button size="small" variant="outlined" startIcon={<FileUploadOutlinedIcon />} disabled={saving} onClick={() => input.current?.click()}>
        Import new {treeType} tree from JSON
      </Button>
      <input ref={input} type="file" accept="application/json,.json" hidden onChange={onFile} />
      {message && (
        <Alert severity={message.severity} sx={{ mt: 1 }} onClose={() => setMessage(null)}>
          {message.text}
          {(message.lines || []).map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </Alert>
      )}
      <SaveDialog
        open={!!pending}
        title={`Create ${pending?.doc?.tree?.name || "tree"} with ${pending?.result?.walkOrder?.length ?? 0} campaigns?`}
        result={pending?.result}
        saving={saving}
        onCancel={() => setPending(null)}
        onConfirm={onConfirm}
      />
    </Box>
  );
};

export default ImportNewTreeButton;
